/**
 * WorkflowInspector - Painel de configuração do bloco/ligação selecionado
 *
 * Concentra a configuração exigida pelo motor v2:
 *  - Gatilho do processo no bloco de Início (o evento saiu da linha);
 *  - Saídas do bloco (para onde vai, com condições) — antes só na aresta;
 *  - Gateways: exclusivo, paralelo (fork) e junção (all / any / n de m);
 *  - Espera/temporizadores: evento, duração, data ou "evento ou prazo";
 *  - Ações do node: formulários guiados montados a partir do catálogo.
 *
 * @version 2.9.1
 * RELEASE: 2026-10-02 v2.9.1 - Traduz os módulos técnicos `vehicle` e
 *          `checklist` no seletor de ações para "Veículos" e "Vistorias".
 * @version 2.9.0
 * RELEASE: 2026-09-04 v2.9.0 - Espera/Exigência: novo campo "Como liberar"
 *          (`config.event_match`) com "Basta um dos acontecimentos" (padrão) e
 *          "Exigir todos os acontecimentos". Aparece apenas quando o bloco
 *          espera dois ou mais eventos.
 * @version 2.8.2
 * RELEASE: 2026-09-04 v2.8.2 - Exigência: reverte v2.8.1. "Eventos esperados"
 *          volta a ser exibido na seção Configuração da espera. A seção
 *          "Liberar quando acontecer" do ExigenciaEditor foi oculta.
 * @version 2.8.1
 * RELEASE: 2026-09-04 v2.8.1 - Exigência: "Eventos esperados" oculto na seção
 *          Configuração da espera (mesmo campo `event_codes` já editado em
 *          "Liberar quando acontecer" — elimina a duplicidade de informação).
 * @version 2.8.0
 * RELEASE: 2026-09-04 v2.8.0 - Seção "Saídas do bloco" removida do sheet dos
 *          blocos Decisão/Execução paralela: o caminho passa a ser configurado
 *          exclusivamente na própria linha do mapa (EdgeForm), eliminando a
 *          duplicidade de informação. Props onSaveSaida/onDeleteSaida removidas.
 * @version 2.7.0
 * RELEASE: 2026-09-01 v2.7.0 - Bloco de Início: modo de avaliação do gatilho
 *          ("no acontecimento" x "quando entrar na condição"), reentrada do
 *          mesmo registro e aviso quando o filtro usa dados pós-criação.
 * @version 2.6.0
 * RELEASE: 2026-08-31 v2.6.0 - Bloco de Exigência com catálogo dinâmico de ações
 *          bloqueáveis, múltiplos eventos de liberação e modelos prontos.
 * @version 2.5.0
 * RELEASE: 2026-08-31 v2.5.0 - Ações no cabeçalho, filtro do gatilho, construtor
 *          de condições (bloco Condição com Sim/Não e condição na ligação) e
 *          bloco de Exigência (pré-requisito que bloqueia ações do CRM).
 * @version 2.4.0

 * RELEASE: 2026-08-31 v2.4.0 - Painel do bloco focado apenas na configuração do
 *          próprio bloco; saídas passam a ser exclusivas do mapa/linha.
 * @version 2.3.0
 * RELEASE: 2026-08-31 v2.3.0 - Painel passa a viver dentro de um cartão
 *          suspenso (bordas arredondadas); o container cuida de borda/sombra.
 * @version 2.2.0
 * RELEASE: 2026-08-31 v2.2.0 - Painel mais largo (420px), gatilho no padrão
 *          Monday (busca + chips) e demais textos trocados por ajuda "?".
 * RELEASE: 2026-08-31 v2.1.0 - Painel compacto (340px), botão de fechar e
 *          textos explicativos trocados por ajuda "?" ao lado do campo.
 * RELEASE: 2026-08-31 v2.0.0 - Saídas configuradas no bloco, formulários
 *          guiados no lugar do JSON e evento removido da ligação.
 * RELEASE: 2026-08-30 v1.0.0 - Criação inicial (designer v2)
 */


import { useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { Plus, Trash2, Save, Zap, X, Search, Check, MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
// v3.7.0: orientação escrita para o responsável da pendência
import { Textarea } from '@/components/ui/textarea';

import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { agruparEventosPorModulo } from './workflowEventGroups';
import { NODE_APPEARANCE } from './workflowNodeMeta';
import { ActionParamForms } from './inspector/ActionParamForms';
// `SaidaPatch` continua em uso nas props (a página segue salvando as linhas)
import { type SaidaPatch } from './inspector/NodeBranchesForm';
import { Ajuda } from './inspector/Ajuda';
import { AvisoInline } from './inspector/AvisoInline';

import { SectionCard, CompactLabel } from './inspector/SectionCard';

import type { WorkflowPaletteItem } from './workflowNodeMeta';
// Normaliza o código de módulo do catálogo (`tasks` = `task`)
import { normalizarModulo } from './taskEntity';
import { ExigenciaEditor } from './inspector/ExigenciaEditor';
import { HumanTaskGuardSection } from './inspector/HumanTaskGuardSection';

import { UserMultiSelect } from './inspector/UserMultiSelect';
import {
  normalizarCondicao,
  serializarCondicao,
  type CondicaoWorkflow,
} from './inspector/WorkflowConditionBuilder';
// v3.0.0: filtros passam a ser editados em modal amplo, com resumo no sheet
import { WorkflowConditionField } from './inspector/WorkflowConditionField';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';


import type {
  WorkflowActionConfig,
  WorkflowCatalogItem,
  WorkflowEditorEdge,
  WorkflowEditorNode,
} from '@/hooks/useWorkflowDesigner';

// =============================================================================
// COMPONENTES AUXILIARES DE UI — leiaute compacto "Sidebar produtiva"
// `SectionCard` e `CompactLabel` foram extraídos para `inspector/SectionCard`
// para que os modais do módulo (criação/configurações) usem o mesmo padrão.
// =============================================================================


/** Botão de fechar do painel (o mapa volta a ocupar a tela inteira) */
function BotaoFechar({ onClose }: { onClose?: () => void }) {
  if (!onClose) return null;
  return (
    <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onClose} aria-label="Fechar painel">
      <X className="h-4 w-4" />
    </Button>
  );
}

/** Menu "…" do cabeçalho com a ação destrutiva (padrão Monday) */
function MenuDeExclusao({ onDelete, rotulo }: { onDelete: () => void; rotulo: string }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" aria-label="Mais ações">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={onDelete}>
          <Trash2 className="mr-2 h-4 w-4" /> {rotulo}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}




// v2.6.0: o editor do bloco de Exigência foi extraído para
// `inspector/ExigenciaEditor.tsx`, agora com catálogo dinâmico de ações
// bloqueáveis e múltiplos eventos de liberação.




interface WorkflowInspectorProps {
  node?: WorkflowEditorNode | null;
  edge?: WorkflowEditorEdge | null;
  nodes: WorkflowEditorNode[];
  /** v2.0.0: todas as ligações, para montar a lista de saídas do bloco */
  edges?: WorkflowEditorEdge[];
  actionCatalog: WorkflowCatalogItem[];
  eventCatalog: WorkflowCatalogItem[];
  onSaveNode: (patch: Partial<WorkflowEditorNode> & { id: string }) => void;
  onSaveEdge: (patch: Partial<WorkflowEditorEdge> & { id: string }) => void;
  onDeleteNode: (id: string) => void;
  onDeleteEdge: (id: string) => void;
  /** v4.0.0: abre o painel da linha (único editor do caminho) */
  onSelecionarSaida?: (edgeId: string) => void;
  /** v4.0.0: troca a ordem de avaliação dos caminhos do bloco */
  onReordenarSaida?: (edgeId: string, direcao: 'cima' | 'baixo') => void;
  /** v3.5.0: cria um bloco novo já ligado a um caminho deste bloco */
  onCriarDestino?: (item: WorkflowPaletteItem, patch?: Partial<SaidaPatch>) => void;

  isSaving?: boolean;
  /** Fase 8: processos do tenant disponíveis para o bloco de subprocesso */
  definitions?: Array<{ id: string; name: string; active_version_id: string | null }>;
  /** Fecha o painel de configuração (limpa a seleção no mapa) */
  onClose?: () => void;
  /** Entidade-raiz do processo (crm_deal | billing) — define o catálogo de campos dos filtros */
  entidade?: string | null;
  /**
   * v2.2.0 do get_workflow_editor: outros processos ATIVOS da mesma entidade-raiz
   * que já usam algum dos gatilhos. Usado para exibir o aviso de gatilho repetido
   * somente quando o conflito realmente existe.
   */
  triggerConflicts?: Array<{ definition_id: string; name: string; event_codes: string[] }>;
}



export function WorkflowInspector(props: WorkflowInspectorProps) {
  const { node, edge } = props;

  if (edge) return <EdgeForm key={edge.id} {...props} edge={edge} />;
  if (node) return <NodeForm key={node.id} {...props} node={node} />;

  return (
    <div className="flex h-full items-center justify-center bg-card p-6 text-center">
      <p className="text-sm text-muted-foreground">Selecione um bloco ou uma linha no mapa.</p>
    </div>
  );
}

// =============================================================================
// FORMULÁRIO DO NODE
// =============================================================================

function NodeForm({
  node,
  actionCatalog,
  eventCatalog,
  nodes,
  edges,
  onSaveNode,
  onDeleteNode,
  // onSelecionarSaida/onReordenarSaida/onCriarDestino continuam na interface
  // (usados pela página), mas não são mais consumidos pelo painel.


  isSaving,
  definitions,
  onClose,
  entidade,
  triggerConflicts,
}: WorkflowInspectorProps & { node: WorkflowEditorNode }) {

  const [label, setLabel] = useState(node.label);
  const [config, setConfig] = useState<Record<string, any>>(node.config || {});
  const [acoes, setAcoes] = useState<WorkflowActionConfig[]>(node.on_enter_actions ?? []);


  useEffect(() => {
    setLabel(node.label);
    setConfig(node.config || {});
    setAcoes(node.on_enter_actions ?? []);
  }, [node]);

  const setCfg = (chave: string, valor: unknown) => setConfig((atual) => ({ ...atual, [chave]: valor }));

  /**
   * Conflitos reais de gatilho: cruza os eventos escolhidos neste bloco de início
   * com os eventos já usados por outros processos ATIVOS da mesma entidade-raiz
   * (lista vinda do banco em `trigger_conflicts`).
   */
  const conflitosDeGatilho = useMemo(() => {
    const selecionados = Array.isArray(config.trigger_events) ? (config.trigger_events as string[]) : [];
    if (selecionados.length === 0) return [];
    return (triggerConflicts ?? []).filter((c) =>
      (c.event_codes ?? []).some((code) => selecionados.includes(code)),
    );
  }, [config.trigger_events, triggerConflicts]);


  const gatewayKind = String(config.gateway_kind || 'paralelo'); // v3.0.0: só paralelo/juncao
  const waitMode = String(config.wait_mode || 'evento');

  /**
   * v2.6.0 — Eventos aguardados pelo bloco de espera.
   * Os modelos gravam `config.event_codes` (lista); versões antigas gravavam
   * `config.event_code` (único). Aqui lemos os dois e sempre gravamos a lista.
   */
  const eventosDaEspera = useMemo<string[]>(() => {
    if (Array.isArray(config.event_codes)) return config.event_codes as string[];
    return config.event_code ? [String(config.event_code)] : [];
  }, [config.event_codes, config.event_code]);

  const aplicarEventosDaEspera = (lista: string[]) =>
    setConfig((atual) => ({ ...atual, event_codes: lista, event_code: null }));

  /**
   * v2.9.0 — Regra de correspondência dos acontecimentos esperados.
   * 'any'  = basta um deles (padrão histórico, aplicado quando a chave não existe)
   * 'all'  = o bloco só libera quando TODOS forem cumpridos
   */
  const eventMatch = String(config.event_match || 'any');

  const juncoes = useMemo(
    () => nodes.filter((n) => n.node_type === 'gateway' && String(n.config?.gateway_kind) === 'juncao'),
    [nodes],
  );

  const salvar = () => {
    if (!label.trim()) {
      toast.error('Informe o nome da etapa');
      return;
    }
    onSaveNode({ id: node.id, label: label.trim(), config, on_enter_actions: acoes });
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="flex items-start gap-2 border-b px-4 py-3">
        {/* Selo com o ícone do tipo do bloco (padrão Monday) */}
        <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-muted ${NODE_APPEARANCE[node.node_type]?.accent ?? ''}`}>
          {(() => {
            const Icone = NODE_APPEARANCE[node.node_type]?.icon ?? Zap;
            return <Icone className="h-4 w-4" />;
          })()}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground">{node.label || 'Configuração da etapa'}</p>
          <p className="truncate text-[11px] uppercase tracking-tight text-muted-foreground">
            {NODE_APPEARANCE[node.node_type]?.label ?? node.node_type}
          </p>
        </div>
        {/* v2.5.0: ações no cabeçalho (Salvar principal, Excluir no menu "…") */}
        {/* v3.5.1: botão Salvar reduzido e com peso visual leve para não competir com o conteúdo */}
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1 px-2 text-xs"
          onClick={salvar}
          disabled={isSaving}
        >
          <Save className="h-3 w-3" /> Salvar
        </Button>
        {node.node_type !== 'inicio' && (
          <MenuDeExclusao onDelete={() => onDeleteNode(node.id)} rotulo="Excluir etapa" />
        )}
        <BotaoFechar onClose={onClose} />

      </div>

      <ScrollArea className="min-h-0 flex-1">
        <div className="space-y-4 p-4">
          {/* Seção: Identificação */}
          <SectionCard>
            <div className="space-y-1.5">
              <CompactLabel>Nome da etapa</CompactLabel>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value.slice(0, 120))}
                maxLength={120}
                className="h-9 text-sm"
              />
            </div>
          </SectionCard>

          {/* ---------------------------------------------------------------
              GATILHO DO PROCESSO (bloco de início)
              É aqui que se define O QUE abre a esteira. Sem gatilho, nada inicia.
          ---------------------------------------------------------------- */}
          {node.node_type === 'inicio' && (
            <>
              {/* Explicação longa fica no "?" do título — economiza espaço vertical */}
              <SectionCard
                title="Gatilho do processo"
                help={
                  'O gatilho é o evento que faz esta esteira iniciar. Aparecem eventos de vários módulos (proposta, contrato, financeiro) porque todos se ligam à entidade principal escolhida na criação do processo — ela define de quem é a execução, não o que a inicia. ' +
                  'Quando dois processos ativos usam o mesmo gatilho, o registro entra nos DOIS ao mesmo tempo; nesse caso use filtros complementares (por exemplo "valor > 1000" em um e "valor <= 1000" no outro).'
                }
              >
                <GatilhoEditor
                  eventCatalog={eventCatalog}
                  selecionados={Array.isArray(config.trigger_events) ? config.trigger_events : []}
                  onChange={(lista) => setCfg('trigger_events', lista)}
                />
                {/* v2.2.0: aviso só aparece quando há conflito REAL com outro processo ativo */}
                {conflitosDeGatilho.length > 0 && (
                  <AvisoInline
                    className="mt-2"
                    rotulo="Vários fluxos com o mesmo gatilho"
                    texto={
                      `Estes gatilhos também iniciam: ${conflitosDeGatilho
                        .map((c) => c.name)
                        .join(', ')}. ` +
                      'O registro entra nos DOIS processos ao mesmo tempo e ambos aparecem na aba Resumo. ' +
                      'Para caminhos alternativos, use filtros complementares — como "valor > 1000" em um e "valor <= 1000" no outro.'
                    }
                  />
                )}
              </SectionCard>





              {/* v2.7.0: quando avaliar o gatilho — "no evento" (clássico) ou
                  "quando o registro entrar na condição" (padrão de mercado:
                  entry criteria / re-enrollment). Resolve o caso do negócio que
                  nasce sem valor e só depois atinge a regra. */}
              <SectionCard title="Quando avaliar">
                <div className="space-y-1.5">
                  <CompactLabel help="No evento: a regra é conferida só no instante em que o acontecimento ocorre (um negócio criado com valor zero nunca entra numa regra de valor). Ao entrar na condição: o processo abre no primeiro momento em que a regra passa a ser verdadeira — na criação ou em qualquer alteração posterior do negócio.">
                    Momento da avaliação
                  </CompactLabel>
                  <Select
                    value={String(config.trigger_mode || 'evento')}
                    onValueChange={(v) => setCfg('trigger_mode', v)}
                  >
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="evento">Quando o acontecimento ocorrer</SelectItem>
                      <SelectItem value="entra_condicao">Quando o registro entrar na condição</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* v2.9.0: a reentrada é por TRANSIÇÃO — o registro precisa sair
                    da regra e voltar a atendê-la. Mover de fase, por si só, não
                    reabre um processo já encerrado. */}
                <div className="mt-3 flex items-center justify-between gap-3">
                  <CompactLabel help="Desligado: cada registro entra uma única vez neste processo. Ligado: o registro só entra de novo se DEIXAR de atender à regra e voltar a atendê-la depois — enquanto continuar dentro da regra, mover de fase ou editar o negócio não reabre o processo encerrado.">
                    Permitir nova entrada do mesmo registro
                  </CompactLabel>
                  <Switch
                    checked={config.trigger_reentry === true}
                    onCheckedChange={(v) => setCfg('trigger_reentry', v)}
                  />
                </div>


                {/* v2.8.0: janela de espera antes de o mesmo registro reentrar
                    (anti "entra e sai", padrão de mercado para re-enrollment) */}
                {config.trigger_reentry === true && (
                  <div className="mt-3 space-y-1.5">
                    <CompactLabel help="Tempo mínimo, em minutos, entre o encerramento de uma execução e uma nova entrada do mesmo registro. Zero desliga a espera.">
                      Espera antes de reentrar (minutos)
                    </CompactLabel>
                    <Input
                      type="number"
                      min={0}
                      className="h-9 text-sm"
                      value={String(config.trigger_reentry_cooldown_min ?? 0)}
                      onChange={(e) =>
                        setCfg('trigger_reentry_cooldown_min', Number(e.target.value) || 0)
                      }
                    />
                  </div>
                )}

                {config.trigger_reentry === true && (
                  <AvisoInline
                    className="mt-2"
                    rotulo="Como funciona a nova entrada"
                    texto={
                      'A reentrada só acontece se o registro SAIR da regra e voltar a atendê-la. ' +
                      'Mover de fase ou editar o negócio sem sair da regra não reabre um processo já concluído.'
                    }
                  />
                )}

              </SectionCard>

              {/* v2.8.0: critério de saída — o que fazer quando o registro deixa
                  de atender à regra (ex.: produto removido e valor zera). */}
              <SectionCard title="Se deixar de atender à regra">
                <div className="space-y-1.5">
                  <CompactLabel help="Manter: a execução segue mesmo que o registro não atenda mais à regra. Encerrar: a execução é cancelada e as pendências (aprovações, prazos e ações) são canceladas junto, liberando as travas. Pausar: a execução fica parada e volta a andar sozinha se o registro atender à regra novamente.">
                    Comportamento
                  </CompactLabel>
                  {/* v2.9.0: quando existe filtro de gatilho, o padrão do motor é
                      ENCERRAR o registro que deixa de atender à regra. */}
                  <Select
                    value={String(
                      config.exit_policy ||
                        (config.trigger_filters &&
                        Object.keys(config.trigger_filters as Record<string, unknown>).length > 0
                          ? 'cancelar'
                          : 'manter'),
                    )}
                    onValueChange={(v) => setCfg('exit_policy', v)}
                  >
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manter">Manter a execução</SelectItem>
                      <SelectItem value="cancelar">Encerrar a execução</SelectItem>
                      <SelectItem value="pausar">Pausar até atender de novo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </SectionCard>

              {/* Entrega 2: filtro do gatilho — evita que o processo abra para
                  todo negócio do tenant (ex.: só pipeline X, só valor > Y). */}
              <SectionCard title="Filtro do gatilho">
                <WorkflowConditionField
                  entidade={entidade}
                  valor={config.trigger_filters}
                  onChange={(c) => setCfg('trigger_filters', c)}
                  titulo="Iniciar somente quando"
                  tituloModal="Filtro do gatilho"
                  vazio="Sem filtro — todo evento abre o processo."
                  ajuda={'Filtra o gatilho. Ex.: "Pipeline é um de Cotação" ou "Valor do negócio é maior que 50000". Sem filtro, todo evento abre o processo.'}
                />

                {/* Diagnóstico: campos que só existem DEPOIS da criação do negócio.
                    Advertência compacta (triângulo + tooltip) para não roubar espaço. */}
                {String(config.trigger_mode || 'evento') === 'evento' &&
                  /"field"\s*:\s*"(deal\.value|item\.|proposal\.|billing\.)/.test(
                    JSON.stringify(config.trigger_filters ?? {}),
                  ) && (
                    <AvisoInline
                      className="mt-2"
                      rotulo="Revise o momento da avaliação"
                      texto={
                        'O filtro usa dados que normalmente só existem depois da criação (valor, produtos, propostas ou cobranças). Com a avaliação "no acontecimento", o processo dificilmente vai abrir. Use "Quando o registro entrar na condição".'
                      }
                    />
                  )}
              </SectionCard>

            </>
          )}




          {/* ---------------------------------------------------------------
              EXECUÇÃO PARALELA: abrir caminhos x juntar caminhos
              v3.0.0: a antiga "Junção" virou um MODO deste bloco e o gateway
              exclusivo saiu da paleta (é o bloco de Decisão).
          ---------------------------------------------------------------- */}
          {node.node_type === 'gateway' && (
            <SectionCard title="Comportamento">
              <div className="space-y-1.5">
                <CompactLabel help="Abrir caminhos: dispara TODOS os caminhos ao mesmo tempo. Juntar caminhos: espera os caminhos abertos voltarem antes de continuar. Para escolher só um caminho, use o bloco de Decisão.">
                  Modo do bloco
                </CompactLabel>
                <Select value={gatewayKind} onValueChange={(v) => setCfg('gateway_kind', v)}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paralelo">Abrir caminhos — dispara todos juntos</SelectItem>
                    <SelectItem value="juncao">Juntar caminhos — aguarda os ramos abertos</SelectItem>
                  </SelectContent>
                </Select>
              </div>


              {gatewayKind === 'paralelo' && (
                <div className="space-y-1.5">
                  <CompactLabel help="Indique o bloco de junção que vai reunir os caminhos abertos aqui. Sem junção, cada caminho segue de forma independente até o fim.">
                    Junção correspondente
                  </CompactLabel>
                  <Select
                    value={String(config.join_node_id || 'nenhuma')}
                    onValueChange={(v) => setCfg('join_node_id', v === 'nenhuma' ? '' : v)}
                  >
                    <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nenhuma">Sem junção</SelectItem>
                      {juncoes.map((j) => (
                        <SelectItem key={j.id} value={j.id}>{j.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {gatewayKind === 'juncao' && (
                <>
                  <div className="space-y-1.5">
                    <CompactLabel help="Todos os ramos: só continua quando todos os caminhos chegarem. Primeiro que chegar: segue assim que um caminho chegar. N de M: continua ao atingir a quantidade informada.">
                      Política de junção
                    </CompactLabel>
                    <Select value={String(config.join_policy || 'all')} onValueChange={(v) => setCfg('join_policy', v)}>
                      <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos os ramos</SelectItem>
                        <SelectItem value="any">Primeiro que chegar</SelectItem>
                        <SelectItem value="n_de_m">N de M ramos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {String(config.join_policy) === 'n_de_m' && (
                    <div className="space-y-1.5">
                      <CompactLabel>Quantidade de ramos exigida</CompactLabel>
                      <Input
                        type="number"
                        min={1}
                        value={config.join_count ?? 2}
                        onChange={(e) => setCfg('join_count', Number(e.target.value))}
                        className="h-9 text-sm"
                      />
                    </div>
                  )}
                </>
              )}
            </SectionCard>
          )}

          {/* ---------------------------------------------------------------
              ESPERA / TEMPORIZADOR
          ---------------------------------------------------------------- */}
          {node.node_type === 'espera' && (
            <SectionCard title="Configuração da espera">
              <div className="space-y-1.5">
                <CompactLabel>Modo de espera</CompactLabel>
                <Select value={waitMode} onValueChange={(v) => setCfg('wait_mode', v)}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="evento">Aguardar um evento</SelectItem>
                    <SelectItem value="duracao">Aguardar uma duração</SelectItem>
                    <SelectItem value="data">Aguardar até uma data</SelectItem>
                    <SelectItem value="evento_ou_prazo">Evento ou prazo (o que vier primeiro)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* v2.6.0: o motor aceita vários eventos (`config.event_codes`),
                  liberando no primeiro que ocorrer. O formato antigo
                  (`event_code` único) continua sendo lido e é migrado ao salvar.
                  v2.8.2: mantém "Eventos esperados" na configuração da espera
                  também para Exigências, pois é o mesmo campo editado no
                  inspetor. A seção "Liberar quando acontecer" do ExigenciaEditor
                  foi oculta para não duplicar o campo. */}
              {(waitMode === 'evento' || waitMode === 'evento_ou_prazo') && (
                <>
                  <div className="space-y-1.5">
                    <CompactLabel
                      help={
                        eventMatch === 'all'
                          ? 'O processo fica parado neste bloco até que TODOS os eventos listados aconteçam.'
                          : 'O processo fica parado neste bloco até que um destes eventos aconteça. Se listar mais de um, vale o primeiro que ocorrer.'
                      }
                    >
                      Eventos esperados
                    </CompactLabel>

                    {eventosDaEspera.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {eventosDaEspera.map((code) => (
                          <Badge key={code} variant="secondary" className="gap-1 text-[11px]">
                            {eventCatalog.find((ev) => ev.code === code)?.label ?? code}
                            <button
                              type="button"
                              aria-label="Remover evento"
                              className="text-muted-foreground hover:text-foreground"
                              onClick={() => aplicarEventosDaEspera(eventosDaEspera.filter((c) => c !== code))}
                            >
                              ×
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}

                    <Select
                      value=""
                      onValueChange={(v) =>
                        aplicarEventosDaEspera(
                          eventosDaEspera.includes(v) ? eventosDaEspera : [...eventosDaEspera, v],
                        )
                      }
                    >
                      <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Adicionar evento" /></SelectTrigger>
                      <SelectContent>
                        <OpcoesDeEvento catalogo={eventCatalog} />
                      </SelectContent>
                    </Select>
                  </div>

                  {/* v2.9.0: regra de correspondência — só faz sentido com 2+ eventos */}
                  {eventosDaEspera.length > 1 && (
                    <div className="space-y-1.5">
                      <CompactLabel help="Define quando o bloco libera o processo: no primeiro acontecimento da lista ou somente depois que todos forem cumpridos.">
                        Como liberar
                      </CompactLabel>
                      <Select value={eventMatch} onValueChange={(v) => setCfg('event_match', v)}>
                        <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Basta um dos acontecimentos</SelectItem>
                          <SelectItem value="all">Exigir todos os acontecimentos</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}


                  <div className="space-y-1.5">
                    <CompactLabel help="Tempo máximo de espera. Vazio = espera indefinidamente pelo evento.">
                      Prazo limite
                    </CompactLabel>
                    <div className="relative">
                      <Input
                        type="number"
                        min={1}
                        placeholder="Sem prazo"
                        value={config.timeout_minutes ?? ''}
                        onChange={(e) =>
                          setCfg('timeout_minutes', e.target.value ? Number(e.target.value) : null)
                        }
                        className="h-9 pr-12 text-sm"
                      />
                      <span className="absolute right-3 top-2 text-[10px] font-medium text-muted-foreground">MIN</span>
                    </div>
                  </div>
                </>
              )}

              {(waitMode === 'duracao' || waitMode === 'evento_ou_prazo') && (
                <div className="space-y-1.5">
                  <CompactLabel>Duração</CompactLabel>
                  <div className="relative">
                    <Input
                      type="number"
                      min={1}
                      value={config.duration_minutes ?? 60}
                      onChange={(e) => setCfg('duration_minutes', Number(e.target.value))}
                      className="h-9 pr-12 text-sm"
                    />
                    <span className="absolute right-3 top-2 text-[10px] font-medium text-muted-foreground">MIN</span>
                  </div>
                </div>
              )}

              {waitMode === 'data' && (
                <div className="space-y-1.5">
                  <CompactLabel>Acordar em</CompactLabel>
                  <Input
                    type="datetime-local"
                    value={String(config.wake_at || '')}
                    onChange={(e) => setCfg('wake_at', e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>
              )}

              {/* v3.5.0: os caminhos de saída são editados na seção comum abaixo */}

            </SectionCard>
          )}


          {/* ---------------------------------------------------------------
              TAREFA HUMANA / APROVAÇÃO (Fase 6)
          ---------------------------------------------------------------- */}
          {node.node_type === 'tarefa_humana' && (
            <>
              {/* Execução: o que a pessoa precisa fazer e se o processo espera */}
              <SectionCard title="Execução">
                <div className="space-y-1.5">
                  {/* v3.6.1: aviso das âncoras movido para o "?" para economizar espaço */}
                  <CompactLabel
                    help={
                      String(config.human_kind || 'aprovacao') === 'aprovacao'
                        ? 'Os caminhos Aprovado e Reprovado são criados pelas duas âncoras do próprio bloco no mapa — cada linha já nasce com o desfecho correto.'
                        : undefined
                    }
                  >
                    Tipo da pendência
                  </CompactLabel>
                  <Select value={String(config.human_kind || 'aprovacao')} onValueChange={(v) => setCfg('human_kind', v)}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="aprovacao">Aprovação (aprovar / reprovar)</SelectItem>
                      <SelectItem value="tarefa">Tarefa operacional</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  {/* v3.6.0: rótulo explícito — antes se confundia com a trava do registro */}
                  <CompactLabel help="Bloqueante: o processo fica parado neste bloco até a resposta. Paralela: o processo segue e a pendência continua aberta.">
                    O processo aguarda esta etapa?
                  </CompactLabel>
                  <Select value={String(config.mode || 'bloqueante')} onValueChange={(v) => setCfg('mode', v)}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bloqueante">Sim — o processo para até a resposta</SelectItem>
                      <SelectItem value="nao_bloqueante">Não — o processo segue em paralelo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/*
                  v3.7.0: orientação exibida na pendência (workflow_human_tasks.description).
                  Texto simples, sem HTML, limitado para evitar abuso de payload.
                */}
                <div className="space-y-1.5">
                  <CompactLabel help="Este texto aparece para quem recebe a pendência, explicando o que precisa ser verificado ou por que a ação está travada.">
                    Orientação para o responsável
                  </CompactLabel>
                  <Textarea
                    value={String(config.instructions ?? config.descricao ?? '')}
                    onChange={(e) => setCfg('instructions', e.target.value.slice(0, 1000))}
                    maxLength={1000}
                    rows={3}
                    className="text-sm resize-none"
                    placeholder="Ex.: Verifique a margem do negócio e confirme o desconto com a diretoria antes de aprovar."
                  />
                </div>
              </SectionCard>



              {/* Atribuição e SLA */}
              <SectionCard title="Atribuição e SLA">
                <div className="space-y-1.5">
                  <CompactLabel>Responsável</CompactLabel>
                  <Select
                    value={String(config.assignee_mode || 'vendedor_do_negocio')}
                    onValueChange={(v) => setCfg('assignee_mode', v)}
                  >
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vendedor_do_negocio">Vendedor do negócio</SelectItem>
                      <SelectItem value="responsavel_negocio">Responsável pelo negócio</SelectItem>
                      <SelectItem value="usuario">Usuário fixo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {String(config.assignee_mode) === 'usuario' && (
                  <div className="space-y-1.5">
                    <CompactLabel>Usuário responsável</CompactLabel>
                    {/* v2.6.0: seletor visual no lugar do antigo campo de UUID */}
                    <UserMultiSelect
                      multiple={false}
                      value={config.assignee_ref ? [String(config.assignee_ref)] : []}
                      onChange={(ids) => setCfg('assignee_ref', ids[0] ?? '')}
                      placeholder="Selecione o responsável"
                    />
                  </div>
                )}

                <div className="space-y-1.5">
                  <CompactLabel help="Tempo máximo para concluir a tarefa. Deixe vazio para não ter prazo.">
                    Prazo SLA
                  </CompactLabel>
                  <div className="relative">
                    <Input
                      type="number"
                      min={0}
                      value={config.sla_minutes ?? ''}
                      onChange={(e) => setCfg('sla_minutes', e.target.value === '' ? '' : Number(e.target.value))}
                      className="h-9 pr-12 text-sm"
                    />
                    <span className="absolute right-3 top-2 text-[10px] font-medium text-muted-foreground">MIN</span>
                  </div>
                </div>
              </SectionCard>

              {/* v3.6.0: trava no registro — pergunta diferente do "aguardar" */}
              <HumanTaskGuardSection config={config} setCfg={setCfg} />


              {/* Escalonamento: ação e usuário de destaque quando o prazo estourar */}
              <SectionCard title="Escalonamento" highlight="alert">
                <div className="space-y-1.5">
                  <CompactLabel>Ao estourar o prazo</CompactLabel>
                  <Select value={String(config.on_timeout || 'escalar')} onValueChange={(v) => setCfg('on_timeout', v)}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="escalar">Escalar para outra pessoa</SelectItem>
                      <SelectItem value="aprovar_automatico">Aprovar automaticamente</SelectItem>
                      <SelectItem value="reprovar_automatico">Reprovar automaticamente</SelectItem>
                      <SelectItem value="seguir_ramo">Seguir o caminho padrão</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {String(config.on_timeout || 'escalar') === 'escalar' && (
                  <div className="space-y-1.5">
                    <CompactLabel>Usuário do escalonamento</CompactLabel>
                    {/* v2.6.0: seletor visual no lugar do antigo campo de UUID */}
                    <UserMultiSelect
                      multiple={false}
                      value={config.escalate_to ? [String(config.escalate_to)] : []}
                      onChange={(ids) => setCfg('escalate_to', ids[0] ?? '')}
                      placeholder="Selecione quem recebe o escalonamento"
                    />
                  </div>
                )}
              </SectionCard>
            </>
          )}

          {/* ---------------------------------------------------------------
              SUBPROCESSO (Fase 8)
          ---------------------------------------------------------------- */}
          {node.node_type === 'subworkflow' && (
            <SectionCard title="Subprocesso">
              <div className="space-y-1.5">
                <CompactLabel>Processo executado</CompactLabel>
                <Select
                  value={String(config.definition_id || '')}
                  onValueChange={(v) => setCfg('definition_id', v)}
                >
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Selecione o processo" /></SelectTrigger>
                  <SelectContent>
                    {(definitions ?? []).map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}
                        {d.active_version_id ? '' : ' (sem publicação)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <CompactLabel>Comportamento</CompactLabel>
                <Select value={String(config.mode || 'bloqueante')} onValueChange={(v) => setCfg('mode', v)}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bloqueante">Aguardar o subprocesso terminar</SelectItem>
                    <SelectItem value="nao_bloqueante">Disparar e seguir na hora</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </SectionCard>
          )}

          {/* ---------------------------------------------------------------
              EXIGÊNCIA (pré-requisito) — Entrega 4
              Bloco de espera que, enquanto não for cumprido, bloqueia ações
              do CRM (gerar contrato, criar cobrança, aceitar proposta...).
          ---------------------------------------------------------------- */}
          {node.node_type === 'espera' && String(config.node_kind || '') === 'exigencia' && (
            <ExigenciaEditor config={config} setCfg={setCfg} eventCatalog={eventCatalog} />
          )}

          {/* ---------------------------------------------------------------
              Caminhos do bloco: fora do painel (padrão BPMN dos grandes SaaS —
              Camunda/Bizagi/Zeebe). Caminhos são criados pelo "+" do bloco no
              mapa e configurados clicando na própria linha; nada de dois
              lugares configurando a mesma informação.
              v2.8.0: a seção "Saídas do bloco" (SaidasDoBloco) foi removida
              também dos blocos Decisão/Execução paralela — a linha é o único
              lugar de edição (EdgeForm).
          ---------------------------------------------------------------- */}





          {/* ---------------------------------------------------------------
              AÇÕES DO NODE (executadas na entrada)
          ---------------------------------------------------------------- */}
          {/* v2.9.0: o bloco Início não exibe ações — o Início só dispara o
              processo (gatilho/filtro); ações ficam nas etapas seguintes,
              evitando que o usuário configure tudo no Início sem criar
              nenhuma etapa. */}
          {node.node_type !== 'inicio' && (
            <AcoesEditor acoes={acoes} catalogo={actionCatalog} onChange={setAcoes} entidade={entidade} />
          )}

        </div>
      </ScrollArea>
    </div>
  );

}

// =============================================================================
// EDITOR DE AÇÕES
// =============================================================================

function AcoesEditor({
  acoes,
  catalogo,
  onChange,
  entidade,
}: {
  acoes: WorkflowActionConfig[];
  catalogo: WorkflowCatalogItem[];
  onChange: (lista: WorkflowActionConfig[]) => void;
  /** Entidade-raiz do processo (campos oferecidos nas variáveis extras) */
  entidade?: string | null;
}) {
  /** Gera os parâmetros iniciais a partir do contrato declarado no catálogo */
  const paramsIniciais = (code: string): Record<string, unknown> => {
    const contrato = catalogo.find((c) => c.code === code)?.params_contract;
    if (contrato && typeof contrato === 'object' && !Array.isArray(contrato)) {
      return Object.fromEntries(Object.keys(contrato).map((k) => [k, '']));
    }
    return {};
  };

  return (
    <SectionCard title="Ações ao entrar nesta etapa">
      <div className="flex items-center justify-between">
        <CompactLabel help="As ações são executadas pela fila de ações do motor assim que o processo entra nesta etapa.">
          Ações vinculadas
        </CompactLabel>
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1 px-2 text-xs"
          disabled={catalogo.length === 0}
          onClick={() =>
            onChange([...acoes, { action_code: catalogo[0].code, params: paramsIniciais(catalogo[0].code) }])
          }
        >
          <Plus className="h-3 w-3" /> Ação
        </Button>
      </div>

      {acoes.length === 0 && <p className="text-xs text-muted-foreground">Nenhuma ação configurada.</p>}

      {acoes.map((acao, index) => (
        <div key={index} className="space-y-3 rounded-xl border bg-muted/20 p-3">
          <div className="flex items-start gap-2">
            {/* v2.6.0: escolha em duas etapas — primeiro o tipo (módulo), depois a ação */}
            <div className="min-w-0 flex-1">
              <CompactLabel className="mb-1">Ação</CompactLabel>
              <SeletorDeAcao
                catalogo={catalogo}
                value={acao.action_code}
                onChange={(v) =>
                  onChange(acoes.map((a, i) => (i === index ? { action_code: v, params: paramsIniciais(v) } : a)))
                }
              />
            </div>

            <div className="flex items-center gap-1 pt-5">
              <Ajuda texto={catalogo.find((c) => c.code === acao.action_code)?.description || 'Parâmetros da ação.'} />
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive"
                onClick={() => onChange(acoes.filter((_, i) => i !== index))}
                aria-label="Remover ação"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* v2.0.0: formulários guiados no lugar do antigo campo JSON */}
          <div className="space-y-1">
            <ActionParamForms
              contrato={catalogo.find((c) => c.code === acao.action_code)?.params_contract}
              params={(acao.params ?? {}) as Record<string, unknown>}
              entidade={entidade}
              onChange={(params) => onChange(acoes.map((a, i) => (i === index ? { ...a, params } : a)))}
            />
          </div>

        </div>
      ))}
    </SectionCard>
  );
}


/**
 * SeletorDeAcao - Escolha da ação em duas etapas (tipo/módulo → ação)
 *
 * Evita a lista única e enorme com todas as ações: o usuário escolhe primeiro o
 * módulo (CRM, Tarefas, Notificações...) e só então a ação daquele módulo.
 */
const ROTULOS_MODULO_ACAO: Record<string, string> = {
  crm: 'CRM — Negócios',
  task: 'Tarefas',
  retention: 'Retenção',
  legal: 'Jurídico',
  notification: 'Notificações',
  flow: 'Fluxos',
  contract: 'Contratos',
  proposal: 'Propostas',
  financial: 'Financeiro',
  integration: 'Integrações',
  vehicle: 'Veículos',
  checklist: 'Vistorias',
  service_order: 'Ordem de Serviço',
};

/**
 * Módulo da ação: usa o campo `module` do catálogo ou o prefixo do código.
 * `tasks` é normalizado para `task` para não duplicar o grupo "Tarefas".
 */
function moduloDaAcao(item: WorkflowCatalogItem): string {
  const bruto = (item.module || String(item.code).split('.')[0] || 'outros').toLowerCase();
  return normalizarModulo(bruto) || 'outros';
}

function SeletorDeAcao({
  catalogo,
  value,
  onChange,
}: {
  catalogo: WorkflowCatalogItem[];
  value: string;
  onChange: (code: string) => void;
}) {
  const selecionada = catalogo.find((c) => c.code === value);
  const moduloAtual = selecionada ? moduloDaAcao(selecionada) : '';

  const [modulo, setModulo] = useState(moduloAtual);

  // Mantém o filtro coerente quando a ação muda por fora do componente
  useEffect(() => {
    if (moduloAtual) setModulo(moduloAtual);
  }, [moduloAtual]);

  const modulos = useMemo(() => {
    const unicos = Array.from(new Set(catalogo.map(moduloDaAcao)));
    return unicos.sort((a, b) =>
      (ROTULOS_MODULO_ACAO[a] ?? a).localeCompare(ROTULOS_MODULO_ACAO[b] ?? b, 'pt-BR'),
    );
  }, [catalogo]);

  const acoesDoModulo = useMemo(
    () => catalogo.filter((c) => moduloDaAcao(c) === modulo),
    [catalogo, modulo],
  );

  return (
    <div className="flex-1 space-y-1.5">
      <Select
        value={modulo}
        onValueChange={(m) => {
          // Troca de módulo já posiciona na primeira ação disponível
          setModulo(m);
          const primeira = catalogo.find((c) => moduloDaAcao(c) === m);
          if (primeira) onChange(primeira.code);
        }}
      >
        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Tipo de ação" /></SelectTrigger>
        <SelectContent>
          {modulos.map((m) => (
            <SelectItem key={m} value={m}>{ROTULOS_MODULO_ACAO[m] ?? m}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={value} onValueChange={onChange}>
        <SelectTrigger><SelectValue placeholder="Selecione a ação" /></SelectTrigger>
        <SelectContent>
          {acoesDoModulo.map((item) => (
            <SelectItem key={item.code} value={item.code}>{item.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// =============================================================================
// FORMULÁRIO DA LIGAÇÃO
// =============================================================================

function EdgeForm({
  edge,
  nodes,
  edges,
  eventCatalog,
  onSaveEdge,
  onDeleteEdge,
  isSaving,
  onClose,
  entidade,
}: WorkflowInspectorProps & { edge: WorkflowEditorEdge }) {

  const [label, setLabel] = useState(edge.label ?? '');
  const [eventCode, setEventCode] = useState(edge.event_code ?? '');
  const [isDefault, setIsDefault] = useState(!!edge.is_default);
  const [isRegression, setIsRegression] = useState(!!edge.is_regression);
  // v2.5.0: condição da linha (formato { op, rules } lido pelo motor)
  const [condicoes, setCondicoes] = useState<CondicaoWorkflow>(normalizarCondicao(edge.conditions));

  useEffect(() => {
    setLabel(edge.label ?? '');
    setEventCode(edge.event_code ?? '');
    setIsDefault(!!edge.is_default);
    setIsRegression(!!edge.is_regression);
    setCondicoes(normalizarCondicao(edge.conditions));
  }, [edge]);

  const nomeDoNode = (id: string) => nodes.find((n) => n.id === id)?.label ?? '—';

  /**
   * v4.0.0 — Caminhos irmãos (mesmo bloco de origem).
   * O motor segue a PRIMEIRA linha compatível, então evento repetido no mesmo
   * bloco cria caminho morto: o evento já usado fica desabilitado e o botão
   * Salvar é bloqueado enquanto houver duplicidade exata.
   */
  const irmas = useMemo(
    () => (edges ?? []).filter((e) => e.from_node_id === edge.from_node_id && e.id !== edge.id),
    [edges, edge.from_node_id, edge.id],
  );

  /** Eventos já usados por outra saída deste bloco → rótulo do caminho */
  const eventosUsados = useMemo(() => {
    const mapa = new Map<string, string>();
    irmas.forEach((e) => {
      if (e.event_code) mapa.set(e.event_code, e.label?.trim() || 'outro caminho');
    });
    return mapa;
  }, [irmas]);

  const eventoDuplicado = !!eventCode && eventosUsados.has(eventCode);
  const padraoDuplicado = isDefault && irmas.some((e) => e.is_default);

  /** Salva rótulo, condição e sinalizadores da ligação */
  const salvarLigacao = () =>
    onSaveEdge({
      id: edge.id,
      label: label.trim() || null,
      event_code: eventCode || null,
      conditions: serializarCondicao(condicoes),
      is_default: isDefault,
      is_regression: isRegression,
    } as Partial<WorkflowEditorEdge> & { id: string });




  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="flex items-start gap-2 border-b px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground">Configuração da ligação</p>
          <p className="truncate text-[11px] uppercase tracking-tight text-muted-foreground">
            {nomeDoNode(edge.from_node_id)} → {nomeDoNode(edge.to_node_id)}
          </p>
        </div>
        {/* v2.5.0: ações no cabeçalho (Salvar principal, Excluir no menu "…")
            v4.0.0: bloqueia o salvamento enquanto houver duplicidade exata. */}
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1 px-2 text-xs"
          disabled={isSaving || eventoDuplicado}
          title={eventoDuplicado ? 'Este evento já é usado por outro caminho deste bloco' : undefined}
          onClick={salvarLigacao}
        >
          <Save className="h-3 w-3" /> Salvar
        </Button>

        <MenuDeExclusao onDelete={() => onDeleteEdge(edge.id)} rotulo="Excluir ligação" />
        <BotaoFechar onClose={onClose} />
      </div>

      <ScrollArea className="min-h-0 flex-1">
        <div className="space-y-4 p-4">
          <SectionCard>
            <div className="space-y-1.5">
              <CompactLabel>Rótulo</CompactLabel>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value.slice(0, 120))}
                maxLength={120}
                className="h-9 text-sm"
              />
            </div>
          </SectionCard>

          {/* v2.5.0: condição da ligação — agora gravada de verdade em
              `workflow_edges.conditions` e avaliada por `workflow_eval_conditions`. */}
          <SectionCard title="Condição">
            <WorkflowConditionField
              entidade={entidade}
              valor={condicoes}
              onChange={setCondicoes}
              titulo="Seguir por esta linha quando"
              tituloModal="Condição da ligação"
              vazio="Sem condição — a linha vale sempre."
              ajuda="O processo só segue por esta linha se as condições forem verdadeiras. Sem condição, a linha vale sempre."
            />
          </SectionCard>

          {/* v2.7.0: o evento na ligação NÃO é formato antigo — é o mecanismo do
              motor (`workflow_enter_node` v2.3.0) que assina os eventos das
              linhas de saída quando o bloco fica parado. É assim que um bloco de
              espera consegue ter dois caminhos (ex.: "Pago" e "Sem pagamento"). */}
          <SectionCard title="Evento">
            <div className="space-y-1.5">
              <CompactLabel help="Isto NÃO é o gatilho do processo. Aqui o processo aguarda este evento antes de seguir por esta linha. O que faz o processo começar fica no bloco Início.">
                Aguardar este evento antes de seguir por esta linha
              </CompactLabel>
              <Select value={eventCode || 'nenhum'} onValueChange={(v) => setEventCode(v === 'nenhum' ? '' : v)}>
                <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Sem evento" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nenhum">Sem evento</SelectItem>
                  <OpcoesDeEvento catalogo={eventCatalog} usados={eventosUsados} />
                </SelectContent>
              </Select>
              {eventoDuplicado && (
                <p className="text-[11px] text-warning">
                  Este evento já é usado no caminho "{eventosUsados.get(eventCode)}" — o processo
                  seguiria apenas o primeiro. Escolha outro evento.
                </p>
              )}
            </div>
          </SectionCard>


          <SectionCard title="Comportamento">
            <div className="flex items-center justify-between">
              <CompactLabel help="Usado quando nenhuma outra ligação que sai deste bloco tem a condição atendida. É o caminho do 'Não'.">
                Caminho padrão
              </CompactLabel>
              <Switch checked={isDefault} onCheckedChange={setIsDefault} />
            </div>
            {padraoDuplicado && (
              <p className="text-[11px] text-warning">
                Este bloco já tem outro caminho padrão. Mantenha apenas um "senão".
              </p>
            )}


            <div className="flex items-center justify-between">
              <CompactLabel help="Marca a ligação como volta do processo para uma etapa anterior (ex.: proposta cancelada).">
                Retrocesso
              </CompactLabel>
              <Switch checked={isRegression} onCheckedChange={setIsRegression} />
            </div>
          </SectionCard>
        </div>
      </ScrollArea>
    </div>
  );
}


// =============================================================================
// AUXILIARES DE EVENTO
// =============================================================================


/**
 * Opções do catálogo de eventos agrupadas por módulo (CRM, Propostas, Contratos...).
 * Facilita entender de onde cada evento vem antes de escolher.
 *
 * v4.0.0: `usados` desabilita eventos já consumidos por outra saída do mesmo
 * bloco, evitando caminhos duplicados (o motor só seguiria o primeiro).
 */
function OpcoesDeEvento({
  catalogo,
  usados,
}: {
  catalogo: WorkflowCatalogItem[];
  usados?: Map<string, string>;
}) {
  const grupos = useMemo(() => agruparEventosPorModulo(catalogo), [catalogo]);

  return (
    <>
      {grupos.map((grupo) => (
        <SelectGroup key={grupo.modulo}>
          <SelectLabel>{grupo.modulo}</SelectLabel>
          {grupo.eventos.map((ev) => {
            const emUso = usados?.get(ev.code);
            return (
              <SelectItem key={ev.code} value={ev.code} disabled={!!emUso}>
                {ev.label}
                {emUso ? ` — já usado em "${emUso}"` : ''}
              </SelectItem>
            );
          })}
        </SelectGroup>
      ))}
    </>
  );
}


/**
 * Editor do gatilho do processo (bloco de início) — padrão Monday.
 * Campo compacto que abre uma lista buscável agrupada por módulo; os eventos
 * escolhidos aparecem como chips removíveis logo abaixo.
 * Um ou mais eventos podem abrir a esteira; sem nenhum, o processo nunca inicia.
 */
function GatilhoEditor({
  eventCatalog,
  selecionados,
  onChange,
}: {
  eventCatalog: WorkflowCatalogItem[];
  selecionados: string[];
  onChange: (lista: string[]) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');

  /** Lista agrupada já filtrada pelo texto digitado */
  const grupos = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return agruparEventosPorModulo(eventCatalog)
      .map((grupo) => ({
        ...grupo,
        eventos: termo
          ? grupo.eventos.filter(
              (ev) => ev.label.toLowerCase().includes(termo) || ev.code.toLowerCase().includes(termo),
            )
          : grupo.eventos,
      }))
      .filter((grupo) => grupo.eventos.length > 0);
  }, [eventCatalog, busca]);

  const rotulo = (code: string) => eventCatalog.find((e) => e.code === code)?.label ?? code;

  const alternar = (code: string) => {
    const atual = new Set(selecionados);
    if (atual.has(code)) atual.delete(code);
    else atual.add(code);
    onChange(Array.from(atual));
  };

  return (
    <div className="space-y-2 rounded-xl border bg-card p-3 shadow-sm">
      <div className="flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/15 text-primary">
          <Zap className="h-3.5 w-3.5" />
        </span>
        <Label className="flex items-center gap-1.5 text-xs font-medium">
          Gatilho do processo
          <Ajuda texto={'Escolha o que faz a esteira começar. Ex.: "Negócio criado" abre o processo assim que um novo negócio entra no CRM. Sem gatilho, o processo não pode ser publicado.'} />
        </Label>
        <Badge variant="secondary" className="ml-auto">{selecionados.length}</Badge>
      </div>

      <Popover open={aberto} onOpenChange={setAberto}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-9 w-full justify-start rounded-lg font-normal">
            <Search className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
            {selecionados.length === 0 ? 'Escolher evento…' : 'Adicionar ou remover eventos'}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[320px] rounded-xl p-0">
          <div className="border-b p-2">
            <Input
              value={busca}
              onChange={(e) => setBusca(e.target.value.slice(0, 60))}
              placeholder="Buscar evento"
              className="h-8 rounded-lg"
            />
          </div>
          <div className="max-h-72 overflow-y-auto p-1">
            {grupos.length === 0 && (
              <p className="px-2 py-3 text-xs text-muted-foreground">Nenhum evento encontrado.</p>
            )}
            {grupos.map((grupo) => (
              <div key={grupo.modulo} className="mb-1">
                <p className="px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {grupo.modulo}
                </p>
                {grupo.eventos.map((ev) => {
                  const marcado = selecionados.includes(ev.code);
                  return (
                    <button
                      key={ev.code}
                      type="button"
                      onClick={() => alternar(ev.code)}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted/60"
                    >
                      <Check className={`h-3.5 w-3.5 shrink-0 ${marcado ? 'text-primary' : 'opacity-0'}`} />
                      <span className="truncate">{ev.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {selecionados.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selecionados.map((code) => (
            <Badge key={code} variant="secondary" className="gap-1 rounded-lg py-1 pl-2 pr-1 text-xs font-normal">
              <span className="max-w-[190px] truncate">{rotulo(code)}</span>
              <button
                type="button"
                aria-label={`Remover ${rotulo(code)}`}
                onClick={() => alternar(code)}
                className="rounded p-0.5 hover:bg-background/60"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}


